import { getAccessToken } from '../auth/keycloak';
import { resolveLegacyPortalUrl } from '../legacy-portal';

/** A single legacy top-menu entry, mirroring the backend DTO. */
export interface TopMenuItem {
  url: string;
  title: string;
  styleClass: string | null;
}

/**
 * Resolves the legacy REST API base URL for the given hostname.
 *
 * The legacy backend is served from the same origin as the embedded legacy
 * portal, so this reuses {@link resolveLegacyPortalUrl}.
 */
export function resolveApiBaseUrl(hostname: string): string | undefined {
  const portalUrl = resolveLegacyPortalUrl(hostname);
  if (portalUrl === undefined) {
    return undefined;
  }
  return new URL(portalUrl).origin;
}

/**
 * Fetches the legacy top menu via an authenticated cross-origin REST call.
 *
 * Throws when the hostname has no known API base URL, when there is no valid
 * access token, or when the backend responds with a non-OK status.
 */
export async function fetchTopMenu(hostname: string, signal?: AbortSignal): Promise<TopMenuItem[]> {
  const base = resolveApiBaseUrl(hostname);
  if (!base) {
    throw new Error(`No API base URL for hostname ${hostname}`);
  }

  const token = await getAccessToken();
  signal?.throwIfAborted();
  if (!token) {
    throw new Error('Not authenticated');
  }

  const res = await fetch(`${base}/api/topMenu`, {
    headers: { Authorization: `Bearer ${token}` },
    ...(signal ? { signal } : {})
  });

  if (!res.ok) {
    throw new Error(`topMenu request failed: ${res.status}`);
  }

  return (await res.json()) as TopMenuItem[];
}
